/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */


import { Scope } from '../model/surveyData';
import { InitiativeDataShare, InitiativePlain } from './initiative';
import { SurveyListItem } from './survey';
import { MetricGroup } from './metricGroup';
import { RequesterMessageFormFields } from '../components/message-modal/types';

export enum DataShareStatus {
  Pending = 'pending',
  Accepted = 'accepted',
  Expired = 'expired',
  Revoked = 'revoked',
  Deleted = 'deleted',
}

export enum RequesterType {
  Portfolio = 'portfolio',
}


export interface DataShareCreateBasic {

  /**
   * Custom title of request message to target organization
   * Should be limited to as short title, limited to 255 characters
   */
  title: string,

  /** Custom content that provided additional context */
  content?: string,

  /** Represent the source data initiative, aka Organization, RootInitiative */
  initiativeId: string;

  /** Represents requester entity that can now use shared data */
  requesterId: string;

  /**
   * What requesterId is referencing:
   * - Portfolio (PT)
   * - Company, Financial Organization (CTL or CT) etc.
   * - Share Token etc.
   */
  requesterType: RequesterType;
  /**
   * Constrains of the data that is shared through this request
   */
  dataScope?: DataScope;
}

export enum RestrictionType {
  Subscription = 'subscription',
}

export enum DataScopeAccess {
  Full = 'full',
  Partial = 'partial',
  None = 'none'
}

interface RestrictionSubscriptionData {
  initiativeId: string;
  subscriptionId: string;
}

/**
 * Reason why this share request is restricted.
 *
 * @example Organization is sponsored through subscription, hence some data access
 * is mandatory and cannot be revoked or managed in any way until the restriction
 * is lifted
 */
interface BaseRestriction<T extends RestrictionType, D = unknown> {
  type: T;

  /**
   * Allow to set end date of the restriction
   */
  endDate?: Date;

  /** Additional message for reason/context of this constrain */
  message?: string;

  /** Generic data based on the type value that allow to manage restriction */
  data: D;
}

// Union of all possible restriction types, should be added here as option
export type Restriction = BaseRestriction<RestrictionType.Subscription, RestrictionSubscriptionData>

export enum DataShareScopeView {
  Insights = 'insights',
  Downloads = 'downloads',
  Survey = 'survey',
  Messaging = 'messaging'
}

/**
 * Represent the data share scope.
 *
 * We are union of filters that applied when data lookups are done
 *
 * @Example
 * For initiativeId and children initiativeIds
 * Find all surveys that are within startDate and endDate
 * and have utrs within these scopes
 *
 * At some point it could also support sharing just specific UTRs directly
 *
 */
export interface DataScopeSurvey {
  startDate?: Date;
  endDate?: Date;
  scope?: Partial<Scope>;
  views?: DataShareScopeView[];
  access?: DataScopeAccess;
}
export interface DataScope {
  survey?: DataScopeSurvey;
}

export interface DataShare extends DataShareCreateBasic {

  /** model ObjectId in string format */
  _id: string;

  /**
   * Represent restriction on the share request where initiative cannot modify
   * it directly due to applied restriction.
   *
   * Example is sponsorship of the subscription, that allow the Sponsor to access
   * the data
   */
  restriction?: Restriction;

  /**
   * When share request was accepted
   */
  acceptedDate?: string;

  /**
   * Represent the expiry date of the share request
   * After this period, share request should no longer be valid
   *
   * If end date is not available, then share request never expire and will be
   * valid until manually revoked.
   */
  expiredDate?: string;

  /**
   * Revoked represents when this share request was revoked
   */
  revokedDated?: string;

  /**
   * DeletedDate represents when this share request was revoked aka deleted
   */
  deletedDate?: string;

  /**
   * When data share request was created
   */
  created: string;

  status: DataShareStatus;
}

export type DataShareMin = Pick<DataShare, '_id' | 'initiativeId' | 'status' | 'dataScope'>;

export interface DataShareWithRequester extends DataShare {
  requester: {
    _id: string;
    name: string;
  }
  status: DataShareStatus;
}

export type RequesterLookup = Pick<DataShare, | 'requesterId' | 'requesterType' | 'initiativeId'>

export type RequesterDetails = Pick<DataShare,'requesterId' | 'requesterType' > & { name: string };

export interface CombinedDataScopeAccess extends DataScopeSurvey {
  access: DataScopeAccess;
  views: DataShareScopeView[];
}

export interface DataShareLookup {
  initiative: Pick<InitiativePlain, 'name' | 'permissionGroup' | 'appConfigCode'>,
  list: SurveyListItem[],
  dataShares: DataShareMin[],
  combinedDataScopeAccess?: CombinedDataScopeAccess,
  metricGroups?: Pick<MetricGroup, '_id' | 'groupName' | 'universalTrackers'>[]
}

export interface RequesterMessageRequest {
  initiativeId: string;
  requesterType: string;
  requesterId: string;
  payload: RequesterMessageFormFields;
}

export interface DataShareActions {
  remove: (d: DataShare) => Promise<void>;
  revoke: (d: DataShare) => Promise<void>;
  accept: (d: DataShare) => Promise<void>;
  update: (d: DataShare, dataScope: DataScope) => Promise<void>;
  updateTemplate: (updateDataShare: InitiativeDataShare) => Promise<void>;
}
